import { create } from 'zustand';

// ─── localStorage helpers ────────────────────────────────────────────────────

const LS_KEYS = {
  mode: 'ds-theme-mode',
  preset: 'ds-theme-preset',
  customTheme: 'ds-custom-theme',
  fontSize: 'ds-font-size',
  sidebar: 'ds-sidebar-collapsed',
  favorites: 'ds-favorites',
  recent: 'ds-recent-pages',
};

/**
 * Check if localStorage is available and functional.
 * Returns false in private browsing modes, when quota is exceeded,
 * or when localStorage is otherwise inaccessible.
 */
let _localStorageAvailable = null;

export function isLocalStorageAvailable() {
  if (_localStorageAvailable !== null) return _localStorageAvailable;
  try {
    const testKey = '__ds_ls_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    _localStorageAvailable = true;
  } catch {
    _localStorageAvailable = false;
  }
  return _localStorageAvailable;
}

/** Safe localStorage read — returns null on failure */
function lsGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Safe localStorage write — silently fails if unavailable */
function lsSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // quota exceeded or unavailable — fail silently
  }
}

// ─── Initial state from localStorage ─────────────────────────────────────────

function loadMode() {
  const stored = lsGet(LS_KEYS.mode);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'light';
}

function loadPreset() {
  const stored = lsGet(LS_KEYS.preset);
  return stored || 'default';
}

function loadCustomTheme() {
  try {
    const stored = lsGet(LS_KEYS.customTheme);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch {
    return null;
  }
}

function loadFontSize() {
  const stored = lsGet(LS_KEYS.fontSize);
  if (stored === 'small' || stored === 'medium' || stored === 'large') return stored;
  return 'medium';
}

function loadSidebarCollapsed() {
  const stored = lsGet(LS_KEYS.sidebar);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return false;
}

function loadFavorites() {
  try {
    const stored = lsGet(LS_KEYS.favorites);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    // Filter to valid strings, deduplicate, cap at 20
    const seen = new Set();
    const result = [];
    for (const item of parsed) {
      if (typeof item === 'string' && item && !seen.has(item)) {
        seen.add(item);
        result.push(item);
        if (result.length >= 20) break;
      }
    }
    return result;
  } catch {
    return [];
  }
}

function loadRecentPages() {
  try {
    const stored = lsGet(LS_KEYS.recent);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    // Validate shape, deduplicate by path, cap at 5
    const seen = new Set();
    const result = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item.path === 'string' &&
        typeof item.label === 'string' &&
        typeof item.timestamp === 'number' &&
        !seen.has(item.path)
      ) {
        seen.add(item.path);
        result.push({ path: item.path, label: item.label, timestamp: item.timestamp });
        if (result.length >= 5) break;
      }
    }
    return result;
  } catch {
    return [];
  }
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useDesignSystemStore = create((set, get) => ({
  // ── Theme ──────────────────────────────────────────────────────────────────
  mode: loadMode(),
  preset: loadPreset(),
  customTheme: loadCustomTheme(),
  fontSize: loadFontSize(),

  setMode: (mode) => {
    const validMode = ['light', 'dark', 'system'].includes(mode) ? mode : 'light';
    set({ mode: validMode });
    lsSet(LS_KEYS.mode, validMode);
  },

  toggleMode: () => {
    const current = get().mode;
    const newMode = current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
    set({ mode: newMode });
    lsSet(LS_KEYS.mode, newMode);
  },

  setPreset: (preset) => {
    set({ preset: preset || 'default' });
    lsSet(LS_KEYS.preset, preset || 'default');
  },

  setCustomTheme: (customTheme) => {
    set({ customTheme: customTheme || null });
    if (customTheme && typeof customTheme === 'object' && Object.keys(customTheme).length > 0) {
      lsSet(LS_KEYS.customTheme, JSON.stringify(customTheme));
    } else {
      // Explicitly remove to avoid stale data
      try { localStorage.removeItem(LS_KEYS.customTheme); } catch { /* ignore */ }
    }
  },

  setFontSize: (size) => {
    const valid = ['small', 'medium', 'large'].includes(size) ? size : 'medium';
    set({ fontSize: valid });
    lsSet(LS_KEYS.fontSize, valid);
  },

  /** Load all theme preferences from user profile data (called after login) */
  loadFromUserPreferences: (themePreferences) => {
    if (!themePreferences) return;
    const { mode, preset, customTheme } = themePreferences;
    if (mode && ['light', 'dark', 'system'].includes(mode)) {
      set({ mode });
      lsSet(LS_KEYS.mode, mode);
    }
    if (preset) {
      set({ preset });
      lsSet(LS_KEYS.preset, preset);
    }
    if (customTheme && typeof customTheme === 'object') {
      const ct = { ...customTheme };
      // Remove null values
      Object.keys(ct).forEach(k => { if (ct[k] === null) delete ct[k]; });
      if (Object.keys(ct).length > 0) {
        set({ customTheme: ct });
        lsSet(LS_KEYS.customTheme, JSON.stringify(ct));
      }
      if (ct.fontSize && ['small', 'medium', 'large'].includes(ct.fontSize)) {
        set({ fontSize: ct.fontSize });
        lsSet(LS_KEYS.fontSize, ct.fontSize);
      }
    }
  },

  // ── Sidebar ────────────────────────────────────────────────────────────────
  sidebarCollapsed: loadSidebarCollapsed(),
  mobileDrawerOpen: false, // never persisted — always starts closed

  setSidebarCollapsed: (collapsed) => {
    const value = Boolean(collapsed);
    set({ sidebarCollapsed: value });
    lsSet(LS_KEYS.sidebar, String(value));
  },

  toggleSidebar: () => {
    const newValue = !get().sidebarCollapsed;
    set({ sidebarCollapsed: newValue });
    lsSet(LS_KEYS.sidebar, String(newValue));
  },

  setMobileDrawerOpen: (open) => {
    set({ mobileDrawerOpen: Boolean(open) });
  },

  // ── Favorites ──────────────────────────────────────────────────────────────
  favorites: loadFavorites(),

  addFavorite: (id) => {
    if (!id || typeof id !== 'string') return;
    const current = get().favorites;
    // Remove if already present (to move to front)
    const filtered = current.filter((fav) => fav !== id);
    // Add to front, cap at 20
    const updated = [id, ...filtered].slice(0, 20);
    set({ favorites: updated });
    lsSet(LS_KEYS.favorites, JSON.stringify(updated));
  },

  removeFavorite: (id) => {
    const current = get().favorites;
    const updated = current.filter((fav) => fav !== id);
    set({ favorites: updated });
    lsSet(LS_KEYS.favorites, JSON.stringify(updated));
  },

  isFavorite: (id) => {
    return get().favorites.includes(id);
  },

  // ── Recently Visited ───────────────────────────────────────────────────────
  recentPages: loadRecentPages(),

  addRecentPage: (path, label) => {
    if (!path || typeof path !== 'string') return;
    const pageLabel = typeof label === 'string' ? label : '';
    const current = get().recentPages;
    // Remove existing entry with same path (deduplicate on revisit)
    const filtered = current.filter((page) => page.path !== path);
    // Add to front with current timestamp
    const entry = { path, label: pageLabel, timestamp: Date.now() };
    const updated = [entry, ...filtered].slice(0, 5);
    set({ recentPages: updated });
    lsSet(LS_KEYS.recent, JSON.stringify(updated));
  },
}));
